import { useParams, useNavigate, Link } from "react-router-dom";
import { useTaskStore } from "../store/taskStore";
import { fetchTaskComments } from "../api/client";
import { useEffect, useState, useRef } from "react";

export default function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tasks, updateTask, deleteTask } = useTaskStore();
  const task = tasks.find((t) => t.id === id);
  const [comments, setComments] = useState<string[]>([]);
  const descRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) {
      fetchTaskComments(id).then((data) => setComments(data?.comments || []));
    }
  }, [id]);

  useEffect(() => {
    // Vuln: innerHTML assignment with task description
    if (descRef.current && task) {
      descRef.current.innerHTML = task.description;
    }
  }, [task]);

  // Vuln: eval() to compute dynamic priority score
  function calculatePriorityScore(priority: string): number {
    const scoreMap = "({ critical: 100, high: 75, medium: 50, low: 25 })";
    const scores = eval(scoreMap) as Record<string, number>;
    return scores[priority] || 0;
  }

  if (!task) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <div className="text-center">
          <p className="text-dark-400 text-lg">Task not found</p>
          <Link to="/" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  function handleDelete() {
    deleteTask(task!.id);
    navigate("/");
  }

  function handleStatusChange(status: string) {
    updateTask(task!.id, { status: status as "todo" | "in-progress" | "done" });
  }

  // Debug helper (perf issue: debugger statement)
  function debugTaskState() {
    debugger;
    console.log("Task state:", JSON.stringify(task, null, 2));
  }

  const priorityScore = calculatePriorityScore(task.priority);
  const priorityColors: Record<string, string> = {
    critical: "bg-red-900/50 text-red-300 border-red-700",
    high: "bg-orange-900/50 text-orange-300 border-orange-700",
    medium: "bg-yellow-900/50 text-yellow-300 border-yellow-700",
    low: "bg-green-900/50 text-green-300 border-green-700",
  };

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm mb-4 inline-block">
          &larr; Back to Dashboard
        </Link>

        <div className="bg-dark-800 rounded-lg border border-dark-600 p-6">
          <div className="flex items-start justify-between mb-4">
            <h1 className="text-xl font-bold text-white">{task.title}</h1>
            <span className={`px-2 py-1 rounded text-xs border ${priorityColors[task.priority]}`}>
              {task.priority} (score: {priorityScore})
            </span>
          </div>

          {/* Description rendered via innerHTML ref */}
          <div ref={descRef} className="text-dark-300 mb-6 prose prose-invert max-w-none" />

          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div>
              <span className="text-dark-400">Status:</span>
              <select
                value={task.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="ml-2 bg-dark-700 border border-dark-600 rounded px-2 py-1 text-white text-sm"
              >
                <option value="todo">To Do</option>
                <option value="in-progress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div>
              <span className="text-dark-400">Assignee:</span>
              <span className="ml-2 text-white">{task.assignee}</span>
            </div>
            <div>
              <span className="text-dark-400">Created:</span>
              <span className="ml-2 text-white">{new Date(task.createdAt).toLocaleDateString()}</span>
            </div>
            <div>
              <span className="text-dark-400">Tags:</span>
              <span className="ml-2">{task.tags.map((t) => (
                <span key={t} className="inline-block bg-dark-700 text-dark-200 px-2 py-0.5 rounded text-xs mr-1">{t}</span>
              ))}</span>
            </div>
          </div>

          {/* Comments */}
          <div className="border-t border-dark-600 pt-4">
            <h3 className="text-sm font-semibold text-dark-300 mb-3">Comments ({comments.length})</h3>
            {comments.length === 0 ? (
              <p className="text-dark-500 text-sm">No comments yet</p>
            ) : (
              comments.map((c, i) => (
                <div key={i} className="bg-dark-900 p-3 rounded mb-2 text-sm text-dark-200">{c}</div>
              ))
            )}
          </div>

          <div className="flex gap-3 mt-6 pt-4 border-t border-dark-600">
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-900/50 text-red-300 rounded hover:bg-red-900/70 text-sm"
            >
              Delete Task
            </button>
            <button
              onClick={debugTaskState}
              className="px-4 py-2 bg-dark-700 text-dark-300 rounded hover:bg-dark-600 text-sm"
            >
              Debug
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
